import React, { useState } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { REGION_LABELS, formatPeriod, EmptyState, toggleButtonGroupSx } from './shared';

/**
 * Negative Pricing Heatmap
 *
 * Hour-of-day vs period grid showing frequency of negative pricing per region.
 * Auto-switches between daily (≤2 months) and monthly (>2 months) column granularity.
 */

const getHeatColor = (pct) => {
  if (pct === 0) return '#f8fafc';
  if (pct < 2) return '#f0fdf4';
  if (pct < 5) return '#bbf7d0';
  if (pct < 10) return '#fef3c7';
  if (pct < 15) return '#fde68a';
  if (pct < 20) return '#fcd34d';
  if (pct < 25) return '#f59e0b';
  if (pct < 30) return '#fecaca';
  if (pct < 40) return '#fca5a5';
  if (pct < 50) return '#f87171';
  if (pct < 60) return '#ef4444';
  if (pct < 75) return '#dc2626';
  return '#b91c1c';
};

const NegativePricingHeatmap = ({ data, regions, granularity }) => {
  const [selectedRegion, setSelectedRegion] = useState(null);

  if (!data || data.length === 0) return <EmptyState message="No negative pricing data available" />;

  const regionIds = [...new Set(data.map((d) => d.region_id))].sort();
  const activeRegion = selectedRegion || regionIds[0];

  const regionData = data.filter((d) => d.region_id === activeRegion);
  const periods = [...new Set(regionData.map((d) => d.period))].sort();
  const hours = Array.from({ length: 24 }, (_, i) => i);

  const lookup = {};
  regionData.forEach(({ period, hour, pct_negative }) => {
    lookup[`${period}_${hour}`] = pct_negative;
  });

  const cellSize = granularity === 'daily' ? 24 : 28;
  const labelWidth = 64;

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
        <ToggleButtonGroup
          value={activeRegion}
          exclusive
          onChange={(_, v) => { if (v) setSelectedRegion(v); }}
          size="small"
          sx={toggleButtonGroupSx}
        >
          {regionIds.map((rid) => (
            <ToggleButton key={rid} value={rid}>{REGION_LABELS[rid] || rid}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ display: 'inline-block', minWidth: labelWidth + periods.length * cellSize + 20 }}>
          {/* Period headers */}
          <Box sx={{ display: 'flex', pl: `${labelWidth}px` }}>
            {periods.map((p) => (
              <Box
                key={p}
                sx={{
                  width: cellSize,
                  textAlign: 'center',
                  fontSize: granularity === 'daily' ? '0.55rem' : '0.65rem',
                  color: 'text.secondary',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                {formatPeriod(p, granularity)}
              </Box>
            ))}
          </Box>

          {/* Grid rows */}
          {hours.map((hour) => (
            <Box key={hour} sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: labelWidth, fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500, textAlign: 'right', pr: 1 }}>
                {String(hour).padStart(2, '0')}:00
              </Box>
              {periods.map((p) => {
                const pct = lookup[`${p}_${hour}`] || 0;
                return (
                  <Tooltip key={`${p}_${hour}`} title={`${formatPeriod(p, granularity)} ${String(hour).padStart(2, '0')}:00 — ${pct.toFixed(1)}% negative`} arrow placement="top">
                    <Box sx={{
                      width: cellSize - 2, height: cellSize - 2, m: '1px',
                      bgcolor: getHeatColor(pct), borderRadius: '3px',
                      border: '1px solid rgba(0,0,0,0.04)', cursor: 'default',
                      transition: 'transform 0.1s', '&:hover': { transform: 'scale(1.15)', zIndex: 1 },
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {pct > 5 && (
                        <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: pct > 30 ? '#fff' : '#374151' }}>
                          {pct.toFixed(0)}
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          ))}

          {/* Legend */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2, pl: `${labelWidth}px` }}>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mr: 1 }}>Negative %:</Typography>
            {[0, 5, 10, 20, 30, 50, 75].map((pct) => (
              <Box key={pct} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <Box sx={{ width: 14, height: 14, bgcolor: getHeatColor(pct), borderRadius: '2px', border: '1px solid rgba(0,0,0,0.08)' }} />
                <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>{pct}%</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default NegativePricingHeatmap;
